import { get, isArray, isEmpty, isEqual, isString } from 'lodash';
import moment from 'moment';
import resourcesConfig from '../../resources';
import { storageTypes } from '../../constants/globals';
import { getItem, setItem } from '../../utils/storage';
import { differenceBetween } from '../../utils/fudrLocalizationHelper';
import orderingRoutes from '../../routes/ordering';
import rewardsRoutes from '../../routes/rewards';
import { getOrderingBasePath } from '../../utils/surfacePaths';
import { isNativeWebView } from '../../utils/helpers';
import { nativeEventType, guestModeConfig } from '../../containers/Rewards/config';
import pagesRoutes from '../../routes/pages';
import { appResolutionConfig } from './selectors';
import { onNativeTokenRefreshed, onNativeTokenRefreshFailed } from '../../utils/client';

export const handleLinkUrl = ({ linkUrl, context }) => {
  if (isNativeWebView) {
    const data = {
      shareType: { type: 'link-url', url: linkUrl, context },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(data));
  } else {
    window.open(linkUrl, '_blank');
  }
};

export const checkIfUserNeedsToRedirectToSignup = ({ loginData, requiredFields }) => {
  const fieldSelector = (field) => {
    switch (field) {
      case 'phone':
        return 'primaryMobileNumber';
      default:
        return field;
    }
  };
  const isRequiredFieldsEmpty = requiredFields.some((field) =>
    isEmpty(get(loginData, fieldSelector(field))),
  );
  if (isEmpty(loginData) || isRequiredFieldsEmpty) {
    return true;
  } else {
    return false;
  }
};

export const birthdayRewardAvailableDate = (dateOfBirth, months = 1, type = 'view') => {
  const currentYear = moment().year();
  let startDate = isEqual(type, 'view')
    ? moment(dateOfBirth)
        .set('year', currentYear)
        .subtract(months, 'months')
        .startOf('month')
    : moment(dateOfBirth).set('year', currentYear).startOf('month');
  let endDate = moment(dateOfBirth).set('year', currentYear).endOf('month');
  if (moment().isAfter(endDate)) {
    return { startDate: startDate.add(1, 'year'), endDate: endDate.add(1, 'year').endOf('month') };
  } else {
    return { startDate, endDate: endDate };
  }
};

export const anniversaryRewardAvailableDate = (dateOfanniversary, months = 1, type = 'view') => {
  const currentYear = moment().year();
  let startDate = isEqual(type, 'view')
    ? moment(dateOfanniversary)
        .set('year', currentYear)
        .subtract(months, 'months')
        .startOf('month')
    : moment(dateOfanniversary).set('year', currentYear).startOf('month');
  let endDate = moment(dateOfanniversary).set('year', currentYear).endOf('month');
  if (moment().isAfter(endDate)) {
    return { startDate: startDate.add(1, 'year'), endDate: endDate.add(1, 'year').endOf('month') };
  } else {
    return { startDate, endDate: endDate };
  }
};

export const checkIfBirthdayCouponNotSubscribed = (dateOfBirth, months = 1) => {
  let startDate = birthdayRewardAvailableDate(dateOfBirth, months).startDate;
  let endDate = birthdayRewardAvailableDate(dateOfBirth).endDate;
  if (moment().isAfter(startDate) && moment().isBefore(endDate) && !isEmpty(dateOfBirth)) {
    return true;
  }
};

export const checkIfAnniversaryCouponNotSubscribed = (dateOfanniversary, months = 1) => {
  let startDate = anniversaryRewardAvailableDate(dateOfanniversary, months).startDate;
  let endDate = anniversaryRewardAvailableDate(dateOfanniversary).endDate;
  if (moment().isAfter(startDate) && moment().isBefore(endDate) && !isEmpty(dateOfanniversary)) {
    return true;
  }
};

export const checkGuestFirstLogin = (rewardJoiningDate, brandGuestId) => {
  if (differenceBetween(moment(), rewardJoiningDate) > 0) {
    return false;
  } else if (
    isEqual(getItem(storageTypes.localStorage, 'isWelcomeMessageShown' + brandGuestId), true)
  ) {
    return false;
  } else {
    return true;
  }
};

export const checkIsGuestJoinedRewardPriorUpdate = ({ rewardJoiningDate, rewardUpdateDate }) => {
  const currentDate = moment().format();
  if (
    moment(rewardJoiningDate).isBefore(currentDate) &&
    moment(rewardJoiningDate).isBefore(rewardUpdateDate)
  ) {
    return true;
  } else {
    return false;
  }
};

export const renderList = (numberOfObjects) => {
  const list = [];
  for (let index = 0; index < numberOfObjects; index++) {
    list.push({});
  }
  return list;
};

export const handlePhoneNumber = ({ countryCode, phoneNumber }) => {
  if (window.ReactNativeWebView) {
    const data = {
      shareType: { type: 'mail', url: `tel:${phoneNumber}` },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(data));
  } else {
    window.open(`tel:${countryCode}${phoneNumber}`, '_self');
  }
};

export const getMappingSteps = (length) => {
  switch (length) {
    case 1:
      return [100];
    case 2:
      return [50, 100];
    case 3:
      return [33.6, 67.8, 100];
    default:
      return [25, 25, 25, 25];
  }
};

export const getOrderingRouteContext = (selectedTable, surface = 'app') => {
  const basePath = getOrderingBasePath(surface);
  let scanCode = get(selectedTable, 'scanCode', '');
  if (!isEmpty(scanCode)) {
    return `${basePath}/${orderingRoutes.containers.menu}/${scanCode}`;
  } else {
    return basePath;
  }
};

export const extractDataFromNative = ({
  message,
  history,
  selectedTable,
  handleNativeBackCTA,
  handleLogout,
  handleNativeRouteName,
  handleClearViewCartItems,
  handleUpdateCart,
  handleRestaurantSelection,
  handleBrandGuestData,
  handleDispatchDealOfferData,
  handleUpdateCartFromNative,
  handleDispatchThemeData,
  handleDispatchCommunityContext,
}) => {
  let dataFromApp = get(message, 'data', '');
  if (!isEmpty(dataFromApp)) {
    dataFromApp = dataFromApp && JSON.parse(dataFromApp);
    const { eventType, data, payload = {} } = dataFromApp;
    switch (eventType) {
      case nativeEventType.nativeAppUpdateAvailable:
        setItem(storageTypes.sessionStorage, 'nativeAppUpdateAvailable', data);
        break;
      case nativeEventType.appVersion:
        data && setItem(storageTypes.sessionStorage, 'appVersion', data);
        break;
      case nativeEventType.location:
        const draftLocationCoordinates = {
          lat: get(data, 'coords.latitude'),
          lng: get(data, 'coords.longitude'),
        };

        setItem(storageTypes.localStorage, 'geoLocation', draftLocationCoordinates);
        window.dispatchEvent(new Event('NewLocationSet'));
        // Dispatching this event to listen location change wherever needed
        break;
      case nativeEventType.currentLocation:
        const locationCoordinates = {
          lat: get(data, 'coords.latitude'),
          lng: get(data, 'coords.longitude'),
        };

        window.dispatchEvent(
          new CustomEvent('DraftCurrentLocationSet', { detail: { locationCoordinates } }),
        );
        break;
      case nativeEventType.routeName:
        // Same handler for store Delivery and community slot: native sends
        // `ordering` (resolve via selectedTable) or `ordering/menu/{scanCode}`.
        if (isEqual(data, orderingRoutes.ordering)) {
          let updatedRoute = getOrderingRouteContext(selectedTable);
          history.push(updatedRoute);
        } else {
          const nextPath = `/${rewardsRoutes.rewards}/${data}`;
          const currentPath = get(history, 'location.pathname', '');
          if (currentPath !== nextPath || !isEmpty(get(payload, 'menuItem'))) {
            history.push(nextPath, { payload });
          }
        }
        break;
      case nativeEventType.hardwareBackPress:
        handleNativeBackCTA();
        break;
      case 'pagesInNative':
        setItem(storageTypes.localStorage, 'pagesInNative', data);
        break;
      case nativeEventType.canGoBack:
        setItem(storageTypes.localStorage, nativeEventType.canGoBack, data);
        break;
      case nativeEventType.isNearbyOutletPickerEnabled:
        setItem(storageTypes.localStorage, nativeEventType.isNearbyOutletPickerEnabled, data);
        break;
      case nativeEventType.canExitModule:
        setItem(storageTypes.localStorage, nativeEventType.canExitModule, data);
        break;
      case 'logout':
        handleLogout(data);
        break;
      case 'scrollToTop':
        if (window.pageYOffset > 0) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          setTimeout(() => {
            if (window.pageYOffset > 0) {
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }
          }, 100);
        }
        break;
      case nativeEventType.nativeRouteName:
        handleNativeRouteName(data);
        break;
      case nativeEventType.clearCartAction:
        handleClearViewCartItems();
        break;
      case nativeEventType.updateMenuCart:
        handleUpdateCart(data);
        break;
      case nativeEventType.restaurantSelection:
        handleRestaurantSelection(data);
        break;
      case nativeEventType.brandGuestData:
        handleBrandGuestData(data);
        break;
      case nativeEventType.dispatchDealOfferData:
        handleDispatchDealOfferData(data);
        break;
      case nativeEventType.dispatchCartFromNative:
        handleUpdateCartFromNative(data);
        break;
      case nativeEventType.themeData:
        handleDispatchThemeData(data);
        break;
      case nativeEventType.communityContext:
        if (handleDispatchCommunityContext && !isEmpty(data)) {
          handleDispatchCommunityContext(data);
        }
        break;
      case nativeEventType.communitySlot: {
        const slotSource = get(data, 'slot', data);
        if (handleDispatchCommunityContext && !isEmpty(slotSource) && typeof slotSource === 'object') {
          handleDispatchCommunityContext({ slot: slotSource });
        }
        break;
      }
      case 'loginData': {
        const auth = get(data, 'auth');
        const brandGuest = get(data, 'brandGuest');
        if (!isEmpty(auth)) {
          setItem(storageTypes.localStorage, 'AuthData', auth);
        }
        if (!isEmpty(brandGuest)) {
          setItem(storageTypes.localStorage, 'BrandGuestData', brandGuest);
          handleBrandGuestData && handleBrandGuestData(brandGuest);
        }
        break;
      }
      case nativeEventType.nativeTokenRefreshed:
        onNativeTokenRefreshed(get(data, 'accessToken', ''));
        break;
      case nativeEventType.nativeTokenRefreshFailed:
        onNativeTokenRefreshFailed(new Error(get(data, 'reason', 'native_refresh_failed')));
        break;
      default:
        break;
    }
  }
};

export const checkVIPFlowApplicable = ({ applicableBrandModules, isBetaUser }) => {
  let isVIPPassFlowApplicable = '';
  const isVIPFlowActive = get(
    applicableBrandModules.find((bModule) => bModule.code === 'pass'),
    'isActive',
    false,
  );

  const isVIPFlowActiveForBetaUsers = get(
    applicableBrandModules.find((bModule) => bModule.code === 'pass'),
    'isBeta',
    false,
  );
  if (isVIPFlowActive) {
    if (isVIPFlowActiveForBetaUsers) {
      if (isBetaUser) {
        isVIPPassFlowApplicable = true;
      } else {
        isVIPPassFlowApplicable = false;
      }
    } else {
      isVIPPassFlowApplicable = true;
    }
  } else {
    isVIPPassFlowApplicable = false;
  }
  return isVIPPassFlowApplicable;
};

export const renderCurrenyName = (currencyName) => {
  return !isEmpty(currencyName) ? currencyName.toUpperCase().split(' ').join('\n') : '';
};

export const getGenderFields = (resources) => {
  const brandGenderFields = get(resources, 'genderFields');
  const genderFields = isArray(brandGenderFields)
    ? brandGenderFields.filter((gender) => isString(gender) && !isEmpty(gender))
    : [];
  return isEmpty(genderFields) ? resourcesConfig.genderFields : genderFields;
};

export const checkBrandModuleApplicable = ({ applicableBrandModules, moduleName }) => {
  let isModuleApplicable = false;
  const isModuleActive = get(
    applicableBrandModules.find((bModule) => bModule.code === moduleName),
    'isActive',
    false,
  );
  const isModuleActiveBeta = get(
    applicableBrandModules.find((bModule) => bModule.code === moduleName),
    'isBeta',
    false,
  );

  if (isModuleActive) {
    isModuleApplicable = true;
  } else if (isModuleActiveBeta) {
    isModuleApplicable = true;
  }

  return isModuleApplicable;
};

export const getGuestDisplayName = (guestName, brandResources = {}, isGuestMode = false) => {
  if (isGuestMode) {
    // Get the guest user display name from brand resources, fallback to default
    const guestDisplayName = get(brandResources, 'guestUserNameText', 'Guest User');
    return guestDisplayName;
  }
  return guestName || '';
};

export const isGuestComponentWhitelisted = (componentIdentifier, whitelistedComponents = []) => {
  return whitelistedComponents.includes(componentIdentifier);
};

export const isGuestAPIWhitelisted = (apiIdentifier, whitelistedAPIs = []) => {
  return whitelistedAPIs.includes(apiIdentifier);
};

export const shouldRenderComponentForGuest = (componentIdentifier, isGuestMode = false) => {
  const isAuthenticated =
    !isEmpty(getItem(storageTypes.localStorage, 'GuestLoginData')) ||
    !isEmpty(getItem(storageTypes.localStorage, 'BrandGuestData'));
  // Added BrandGuestData to check if user is authenticated with brand guest login
  // GuestLoginData is used for guest login and BrandGuestData is used for brand guest login

  // If not in guest mode or user is authenticated, render everything
  if (!isGuestMode || isAuthenticated) {
    return true;
  }

  // For guest users, only render whitelisted components
  const whitelistedComponents = get(guestModeConfig, 'whitelistedComponents', []);
  return isGuestComponentWhitelisted(componentIdentifier, whitelistedComponents);
};

export const shouldAllowAPIForGuest = (apiIdentifier, isGuestMode = false) => {
  const isAuthenticated =
    !isEmpty(getItem(storageTypes.localStorage, 'GuestLoginData')) ||
    !isEmpty(getItem(storageTypes.localStorage, 'BrandGuestData'));
  // Added BrandGuestData to check if user is authenticated with brand guest login
  // GuestLoginData is used for guest login and BrandGuestData is used for brand guest login

  // If not in guest mode or user is authenticated, allow all APIs
  if (!isGuestMode || isAuthenticated) {
    return true;
  }

  // For guest users, only allow whitelisted APIs
  const whitelistedAPIs = get(guestModeConfig, 'whitelistedAPIs', []);
  return isGuestAPIWhitelisted(apiIdentifier, whitelistedAPIs);
};

/** When web is not applicable: `defaultWebRedirectionUrl` when set, else Get App path. */
export const buildRedirectWhenWebDisabledUrl = ({
  defaultWebRedirectionUrl,
  getAppPath,
}) => {
  if (!isEmpty(defaultWebRedirectionUrl)) return defaultWebRedirectionUrl;
  return getAppPath;
};

/** `outsideAppResolution` allows web ordering under the Rewards shell. */
export const isOutsideResolutionWebOrdering = (outsideAppResolution) =>
  isEqual(outsideAppResolution, appResolutionConfig.webOrderFlow) ||
  isEqual(outsideAppResolution, appResolutionConfig.webOrderFlowWithLoyalty);

export const buildRouteWithRewardProgramReturn = (route, view) => {
  let updatedRoute = `${route}${route.includes('?') ? '&' : '?'}returnTo=${
    rewardsRoutes.containers.rewardProgramDetails
  }`;
  if (!isEmpty(view)) {
    updatedRoute += `&view=${view}`;
  }
  return updatedRoute;
};

export const buildCouponDetailRouteFromRewardProgram = (couponCode, view) =>
  buildRouteWithRewardProgramReturn(
    `${rewardsRoutes.containers.couponDetail}/${couponCode}`,
    view,
  );

export const rewardProgramReturnNavState = {
  from: rewardsRoutes.containers.rewardProgramDetails,
  initiatorRoute: rewardsRoutes.containers.rewardProgramDetails,
};

export const handlePrivacyPolicyLinkClick = (context, consentConfig = {}, options = {}) => {
  const { privacyPolicyUrl, tncUrl } = consentConfig;
  let linkUrl = '';
  switch (context) {
    case pagesRoutes.containers.privacyPolicy:
      linkUrl = privacyPolicyUrl;
      break;
    case pagesRoutes.containers.termsOfService:
      linkUrl = tncUrl;
      break;
    default:
      break;
  }
  // Site passes openInternalStaticPage so Privacy / TnC stay in StaticPageContentPopup
  // instead of a new tab, including when consent config also has an external URL.
  if (typeof get(options, 'openInternalStaticPage') === 'function' && !isNativeWebView) {
    options.openInternalStaticPage(context);
    return;
  }
  handleLinkUrl({
    linkUrl: linkUrl ? linkUrl : `${window.location.origin}/${pagesRoutes.pages}/${context}`,
  });
};
